import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { AppIconButton } from '../design-system';
import { getMediaScrimColors } from '../../theme/uiSemanticColors';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
} from '@mui/icons-material';
import { downloadTrackedFile, resolveFilePreviewUrls } from '../../utils/filePreview';

export interface SlotImageThumbItem {
  objectKey: string;
  fileId?: number;
  /** Suggested filename for download (defaults to object key basename). */
  downloadFilename?: string;
  primaryCaption?: string | null;
  secondaryCaption?: string | null;
  /** OCR confidence 0–1 (used for caption color when secondaryCaption is omitted). */
  confidence?: number | null;
}

interface SlotImageThumbnailGalleryProps {
  items: SlotImageThumbItem[];
  onRemove?: (objectKey: string) => void;
  disabled?: boolean;
  emptyMessage?: string;
  /** Thumbnail edge length in pixels. */
  thumbSize?: number;
  /** Center thumbnails within the container (e.g. detail panel Files tab). */
  centered?: boolean;
  /** Highlights the thumbnail matching this object key (e.g. OCR table row selection). */
  highlightedObjectKey?: string | null;
  /** Called when a thumbnail is clicked (before opening the lightbox). */
  onItemActivate?: (objectKey: string) => void;
}

const DEFAULT_THUMB_SIZE = 120;

function buildPreviewFetchKey(items: SlotImageThumbItem[]): string {
  return items.map((item) => item.objectKey).join('\n');
}

const SlotImageThumbnailGallery: React.FC<SlotImageThumbnailGalleryProps> = ({
  items,
  onRemove,
  disabled = false,
  emptyMessage,
  thumbSize = DEFAULT_THUMB_SIZE,
  centered = false,
  highlightedObjectKey = null,
  onItemActivate,
}) => {
  const theme = useTheme();
  const mediaScrim = useMemo(() => getMediaScrimColors(theme), [theme]);
  const thumbRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [previewUrls, setPreviewUrls] = useState<Record<string, string | null>>({});
  const [loading, setLoading] = useState(false);
  const [downloadingKey, setDownloadingKey] = useState<string | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const downloadFilenameFor = (item: SlotImageThumbItem): string => {
    if (item.downloadFilename?.trim()) {
      return item.downloadFilename.trim();
    }
    const fromKey = item.objectKey.split('/').pop();
    if (fromKey?.trim()) {
      return fromKey;
    }
    return 'image';
  };

  const handleDownload = async (item: SlotImageThumbItem) => {
    setDownloadingKey(item.objectKey);
    try {
      await downloadTrackedFile({ objectKey: item.objectKey }, downloadFilenameFor(item));
    } catch (err) {
      console.error('Failed to download image', err);
    } finally {
      setDownloadingKey((key) => (key === item.objectKey ? null : key));
    }
  };

  const captionWidth = thumbSize + 16;
  const previewFetchKey = buildPreviewFetchKey(items);
  const previewUrlsRef = useRef(previewUrls);
  previewUrlsRef.current = previewUrls;

  useEffect(() => {
    if (items.length === 0) {
      setPreviewUrls({});
      setLoading(false);
      return;
    }

    let active = true;
    const currentKeys = new Set(items.map((item) => item.objectKey));

    setPreviewUrls((cached) => {
      let changed = false;
      const next = { ...cached };
      for (const key of Object.keys(next)) {
        if (!currentKeys.has(key)) {
          delete next[key];
          changed = true;
        }
      }
      return changed ? next : cached;
    });

    const cached = previewUrlsRef.current;
    const toFetch = items.filter((item) => !(item.objectKey in cached));

    if (toFetch.length === 0) {
      setLoading(false);
      return () => {
        active = false;
      };
    }

    setLoading(true);
    void resolveFilePreviewUrls(toFetch.map((item) => ({ objectKey: item.objectKey })))
      .then((urls) => {
        if (!active) return;
        setPreviewUrls((prev) => {
          const next = { ...prev };
          toFetch.forEach((item, index) => {
            next[item.objectKey] = urls[index] ?? null;
          });
          return next;
        });
      })
      .catch((err) => {
        console.error('Failed to resolve image previews', err);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [previewFetchKey, items]);

  useEffect(() => {
    setLightboxIndex((index) => {
      if (index == null || index < items.length) {
        return index;
      }
      return null;
    });
  }, [previewFetchKey, items.length, items]);

  useEffect(() => {
    if (!highlightedObjectKey) {
      return;
    }
    const el = thumbRefs.current[highlightedObjectKey];
    el?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [highlightedObjectKey, previewFetchKey]);

  const openLightbox = (index: number) => {
    const url = previewUrls[items[index]?.objectKey];
    if (url) {
      setLightboxIndex(index);
    }
  };

  const closeLightbox = () => setLightboxIndex(null);

  const goToPrevImage = useCallback(() => {
    setLightboxIndex((i) => (i != null && i > 0 ? i - 1 : i));
  }, []);

  const goToNextImage = useCallback(() => {
    setLightboxIndex((i) => (i != null && i < items.length - 1 ? i + 1 : i));
  }, [items.length]);

  useEffect(() => {
    if (lightboxIndex == null) {
      return;
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goToPrevImage();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goToNextImage();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [lightboxIndex, goToPrevImage, goToNextImage]);

  const lightboxItem = lightboxIndex != null ? items[lightboxIndex] : null;
  const lightboxUrl = lightboxItem ? previewUrls[lightboxItem.objectKey] : null;

  if (items.length === 0) {
    if (!emptyMessage) {
      return null;
    }
    return (
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 1, textAlign: centered ? 'center' : 'left' }}
      >
        {emptyMessage}
      </Typography>
    );
  }

  const showGlobalLoading =
    loading && items.every((item) => previewUrls[item.objectKey] == null);

  return (
    <Box sx={{ position: 'relative' }}>
      {showGlobalLoading ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            py: 1,
            justifyContent: centered ? 'center' : 'flex-start',
          }}
        >
          <CircularProgress size={16} />
          <Typography variant="caption" color="text.secondary">
            Loading previews…
          </Typography>
        </Box>
      ) : null}
      <Stack
        direction="row"
        spacing={1.5}
        flexWrap="wrap"
        useFlexGap
        justifyContent={centered ? 'center' : 'flex-start'}
      >
        {items.map((item, index) => {
          const url = previewUrls[item.objectKey];
          const conf = item.confidence;
          const confPct = conf != null ? `${(conf * 100).toFixed(0)}%` : null;
          const confColor =
            conf == null
              ? 'text.disabled'
              : conf >= 0.85
                ? 'success.main'
                : conf >= 0.6
                  ? 'warning.main'
                  : 'error.main';
          const secondaryLabel = item.secondaryCaption ?? (confPct ? `${confPct} confidence` : null);
          const canOpen = Boolean(url);
          const isDownloading = downloadingKey === item.objectKey;
          const isHighlighted = highlightedObjectKey === item.objectKey;

          return (
            <Box
              key={item.objectKey}
              ref={(node: HTMLDivElement | null) => {
                thumbRefs.current[item.objectKey] = node;
              }}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: captionWidth,
                gap: 0.5,
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  width: thumbSize,
                  height: thumbSize,
                  '& .thumb-actions': {
                    opacity: isDownloading ? 1 : 0,
                    pointerEvents: isDownloading ? 'auto' : 'none',
                    transition: 'opacity 0.15s ease',
                  },
                  '&:hover .thumb-actions, &:focus-within .thumb-actions': {
                    opacity: 1,
                    pointerEvents: 'auto',
                  },
                }}
              >
                <Box
                  role={canOpen ? 'button' : undefined}
                  tabIndex={canOpen ? 0 : undefined}
                  aria-label={canOpen ? 'View full size image' : undefined}
                  onClick={() => {
                    onItemActivate?.(item.objectKey);
                    if (canOpen) {
                      openLightbox(index);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onItemActivate?.(item.objectKey);
                      if (canOpen) {
                        openLightbox(index);
                      }
                    }
                  }}
                  sx={{
                    width: '100%',
                    height: '100%',
                    borderRadius: 1,
                    border: '2px solid',
                    borderColor: isHighlighted ? 'primary.main' : 'divider',
                    overflow: 'hidden',
                    bgcolor: isHighlighted ? 'action.selected' : 'action.hover',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: canOpen || onItemActivate ? 'pointer' : 'default',
                    boxShadow: isHighlighted ? 3 : undefined,
                    ...(canOpen &&
                      !isHighlighted && {
                        '&:hover': {
                          borderColor: 'primary.main',
                          boxShadow: 1,
                        },
                      }),
                  }}
                >
                  {url ? (
                    <Box
                      component="img"
                      src={url}
                      alt={item.primaryCaption ?? 'Slot image'}
                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : loading ? (
                    <CircularProgress size={22} />
                  ) : (
                    <Typography variant="caption" color="text.disabled" sx={{ px: 0.5, textAlign: 'center' }}>
                      No preview
                    </Typography>
                  )}
                </Box>
                <Box
                  className="thumb-actions"
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    display: 'flex',
                    gap: 0.25,
                    zIndex: 1,
                  }}
                >
                  <AppIconButton
                    size="small"
                    aria-label="Download image"
                    disabled={isDownloading}
                    onClick={(e) => {
                      e.stopPropagation();
                      void handleDownload(item);
                    }}
                    sx={{
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                      width: 24,
                      height: 24,
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    {isDownloading ? (
                      <CircularProgress size={14} />
                    ) : (
                      <DownloadIcon sx={{ fontSize: 16 }} />
                    )}
                  </AppIconButton>
                  {onRemove && !disabled ? (
                    <AppIconButton
                      size="small"
                      aria-label="Remove image"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemove(item.objectKey);
                      }}
                      sx={{
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        width: 24,
                        height: 24,
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </AppIconButton>
                  ) : null}
                </Box>
              </Box>
              {item.primaryCaption ? (
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: 'monospace',
                    textAlign: 'center',
                    lineHeight: 1.15,
                    maxWidth: captionWidth,
                  }}
                  noWrap
                  title={item.primaryCaption}
                >
                  {item.primaryCaption}
                </Typography>
              ) : null}
              {secondaryLabel ? (
                <Typography variant="caption" sx={{ color: confColor, lineHeight: 1, fontSize: '0.7rem' }}>
                  {secondaryLabel}
                </Typography>
              ) : null}
            </Box>
          );
        })}
      </Stack>

      <Dialog
        open={lightboxIndex != null}
        onClose={closeLightbox}
        maxWidth="lg"
        fullWidth
        disableEnforceFocus
      >
        <DialogTitle
          component="div"
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5 }}
        >
          <Typography variant="subtitle1" fontWeight={600} component="span">
            {lightboxItem?.primaryCaption
              ? `Image — ${lightboxItem.primaryCaption}`
              : `Image ${(lightboxIndex ?? 0) + 1} of ${items.length}`}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {lightboxItem ? (
              <AppIconButton
                size="small"
                aria-label="Download image"
                disabled={downloadingKey === lightboxItem.objectKey}
                onClick={() => void handleDownload(lightboxItem)}
              >
                {downloadingKey === lightboxItem.objectKey ? (
                  <CircularProgress size={20} />
                ) : (
                  <DownloadIcon />
                )}
              </AppIconButton>
            ) : null}
            <AppIconButton onClick={closeLightbox} size="small" aria-label="Close">
              <CloseIcon />
            </AppIconButton>
          </Box>
        </DialogTitle>
        <DialogContent
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'common.black',
            minHeight: 400,
            p: 2,
            position: 'relative',
          }}
        >
          {items.length > 1 && lightboxIndex != null ? (
            <>
              <AppIconButton
                onClick={goToPrevImage}
                disabled={lightboxIndex === 0}
                aria-label="Previous image"
                sx={{
                  position: 'absolute',
                  left: 8,
                  color: 'common.white',
                  bgcolor: mediaScrim.control,
                  '&:hover': { bgcolor: mediaScrim.controlHover },
                }}
              >
                <PrevIcon />
              </AppIconButton>
              <AppIconButton
                onClick={goToNextImage}
                disabled={lightboxIndex === items.length - 1}
                aria-label="Next image"
                sx={{
                  position: 'absolute',
                  right: 8,
                  color: 'common.white',
                  bgcolor: mediaScrim.control,
                  '&:hover': { bgcolor: mediaScrim.controlHover },
                }}
              >
                <NextIcon />
              </AppIconButton>
            </>
          ) : null}
          {lightboxUrl ? (
            <Box
              component="img"
              src={lightboxUrl}
              alt={lightboxItem?.primaryCaption ?? 'Full size'}
              sx={{
                maxWidth: '100%',
                maxHeight: 'min(75vh, 720px)',
                objectFit: 'contain',
              }}
            />
          ) : (
            <CircularProgress sx={{ color: 'common.white' }} />
          )}
        </DialogContent>
        {lightboxItem && (lightboxItem.primaryCaption || lightboxItem.confidence != null) ? (
          <Box sx={{ px: 3, pb: 2, pt: 0 }}>
            {lightboxItem.primaryCaption ? (
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                OCR: {lightboxItem.primaryCaption}
              </Typography>
            ) : null}
            {lightboxItem.confidence != null ? (
              <Typography variant="caption" color="text.secondary">
                Confidence: {(lightboxItem.confidence * 100).toFixed(0)}%
              </Typography>
            ) : null}
          </Box>
        ) : null}
      </Dialog>
    </Box>
  );
};

export default SlotImageThumbnailGallery;
